import { lazy, Suspense, FC, ReactNode, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import dibsCss from 'dibs-css';
import { trackEvent } from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';

import { getHolidayShippingInfo } from './helpers/holidayDeliveryHelpers';
import {
    getHolidayShippingAbTestVariant,
    trackHolidayShippingAbTestVariant,
} from './helpers/holidayDeliveryAbTestHelpers';

// types
import { PdpSharedShippingHolidayDelivery_sku$data as Sku } from './__generated__/PdpSharedShippingHolidayDelivery_sku.graphql';
import { PdpSharedShippingHolidayDelivery_item$data as Item } from './__generated__/PdpSharedShippingHolidayDelivery_item.graphql';
import { PdpSharedShippingHolidayDelivery_viewer$data as Viewer } from './__generated__/PdpSharedShippingHolidayDelivery_viewer.graphql';

// styles
import styles from './PdpSharedShippingHolidayDelivery.scss';

const PdpSharedShippingHolidayDeliveryModal = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryModal" */ './PdpSharedShippingHolidayDeliveryModal'
        )
);

const PdpSharedShippingHolidayDeliveryTooltip = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryTooltip" */ './PdpSharedShippingHolidayDeliveryTooltip'
        )
);

const BowIcon = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryBowIcon" */ 'dibs-icons/exports/legacy/Bow'
        )
);

const trackHolidayDelivery = ({
    isFreeShipping,
    standardShipping,
}: {
    isFreeShipping: boolean;
    standardShipping: boolean;
}): void => {
    let shippingType = 'expedited';
    if (isFreeShipping) {
        shippingType = 'free';
    } else if (standardShipping) {
        shippingType = 'standard';
    }

    trackEvent({
        category: 'product',
        label: `${shippingType} shipping`,
        action: 'holiday get in time shipping displayed',
    });
};

type Props = {
    sku: Sku | null | undefined;
    item: Item;
    viewer: Viewer;
    isClient: boolean;
    useIcon?: boolean;
    useTooltipDetails?: boolean;
};

export const PdpSharedShippingHolidayDeliveryComponent: FC<Props> = ({
    sku,
    item,
    viewer,
    useIcon,
    isClient,
    useTooltipDetails,
}) => {
    const [hasFiredTracking, setHasFiredTracking] = useState<boolean>(false);
    const { relevantShipping, transactionWarnings } = viewer;
    const { hasRestrictedMaterials, isCheckoutBlocked } = transactionWarnings || {};
    const shipmentDeliveryEstimations = sku
        ? sku?.holidayShippingEstimations
        : item?.holidayShippingEstimations;
    const {
        displayMessage,
        termsAndConditions = null,
        validHolidayShippingDays = null,
    } = shipmentDeliveryEstimations || {};

    const holidayShippingMethods = getHolidayShippingInfo({
        validHolidayShippingDays,
        shipmentQuotes: relevantShipping?.shipmentQuotes || [],
    });

    if (
        !holidayShippingMethods.length ||
        hasRestrictedMaterials ||
        isCheckoutBlocked ||
        !displayMessage ||
        item?.isAuctionEnabled
    ) {
        return null;
    }

    const variant = getHolidayShippingAbTestVariant();
    const isFirstVariant = variant === 'variant-1';

    const { totalAmount, isComplimentary, estimatedDeliveryDay, buyerServiceMethodDisplay } =
        holidayShippingMethods[0];

    const isFreeShipping = isComplimentary || totalAmount?.amount === 0;

    if (!hasFiredTracking && isClient) {
        if (variant) {
            trackHolidayShippingAbTestVariant();
        }

        if (!isFirstVariant) {
            trackHolidayDelivery({
                isFreeShipping,
                standardShipping: buyerServiceMethodDisplay === 'Standard',
            });
        }

        setHasFiredTracking(true);
    }

    if (isFirstVariant) {
        return null;
    }

    const isVariantTwo = variant === 'variant-2';

    return (
        <div className={styles.container}>
            {useIcon && isClient && (
                <Suspense fallback={<div className={styles.iconComponentSpacer} />}>
                    <div className={styles.iconComponentSpacer}>
                        <BowIcon className={styles.iconComponent} />
                    </div>
                </Suspense>
            )}
            <span className={styles.text}>
                <FormattedMessage
                    id="pdp.sharedHolidayShipping.estimatedDelivery.abTest.copy"
                    defaultMessage="<boldText>{displayMessage}:</boldText>{isFreeShipping, select,
                        true { Free}
                        other {}
                        } {isVariantTwo, select,
                            true {Arrives by <estimatedDeliveryDayWrapper>{estimatedDeliveryDay}{infoTooltip}</estimatedDeliveryDayWrapper> } 
                            other {{buyerServiceMethodDisplay} Arrives by <estimatedDeliveryDayWrapper>{estimatedDeliveryDay}{infoTooltip}</estimatedDeliveryDayWrapper>}
                        }"
                    values={{
                        isVariantTwo,
                        // we want to ignore free shipping label in the second variant of the holiday shipping ab test
                        isFreeShipping: !isVariantTwo && isFreeShipping,
                        displayMessage,
                        buyerServiceMethodDisplay,
                        boldText: (chunks: ReactNode[]) => (
                            <span className={styles.boldText}>{chunks}</span>
                        ),
                        estimatedDeliveryDay,
                        estimatedDeliveryDayWrapper: content => (
                            <span className={dibsCss.whitespaceNowrap}>{content}</span>
                        ),
                        infoTooltip: useTooltipDetails && isClient && (
                            <span key="infoTooltipWrapper" className={dibsCss.whitespaceNormal}>
                                <Suspense fallback={null}>
                                    <PdpSharedShippingHolidayDeliveryTooltip
                                        termsAndConditions={termsAndConditions}
                                    />
                                </Suspense>
                            </span>
                        ),
                    }}
                />
                {!useTooltipDetails && isClient && (
                    <Suspense fallback={null}>
                        {' '}
                        <PdpSharedShippingHolidayDeliveryModal
                            termsAndConditions={termsAndConditions}
                        />
                    </Suspense>
                )}
            </span>
        </div>
    );
};

export const PdpSharedShippingHolidayDelivery = createFragmentContainer(
    PdpSharedShippingHolidayDeliveryComponent,
    {
        viewer: graphql`
            fragment PdpSharedShippingHolidayDelivery_viewer on Viewer {
                transactionWarnings(itemId: $itemId, country: $userCountryCode)
                    @include(if: $isClient) {
                    hasRestrictedMaterials
                    isCheckoutBlocked
                }
                relevantShipping(
                    itemId: $itemId
                    buyerId: $userId
                    quantity: $itemQuantity
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                    fdfQuoteABTestVariant: $fdfQuoteABTestVariant
                ) @include(if: $isClient) {
                    shipmentQuotes {
                        serviceId
                        isComplimentary
                        buyerServiceMethodDisplay
                        totalAmount {
                            amount
                        }
                        serviceMethod {
                            serviceType
                            serviceMethodType
                            region {
                                code
                            }
                        }
                    }
                }
            }
        `,
        item: graphql`
            fragment PdpSharedShippingHolidayDelivery_item on Item {
                isAuctionEnabled
                holidayShippingEstimations(timezoneOffset: $timezoneOffset)
                    @include(if: $isClient) {
                    displayMessage
                    termsAndConditions
                    validHolidayShippingDays {
                        shipmentQuoteId
                        estimatedDeliveryDay
                    }
                }
            }
        `,
        sku: graphql`
            fragment PdpSharedShippingHolidayDelivery_sku on ItemSkuType {
                holidayShippingEstimations(timezoneOffset: $timezoneOffset)
                    @include(if: $isClient) {
                    displayMessage
                    termsAndConditions
                    validHolidayShippingDays {
                        shipmentQuoteId
                        estimatedDeliveryDay
                    }
                }
            }
        `,
    }
);
